/**
 * THE canonical platform defaults for every hierarchy-resolved config field
 * (ADR 0035). One table; every consumer default in the codebase either reads
 * this or is a deliberate, commented FORM SEED (an admin form's suggested
 * starting value — e.g. the wizard's maxPlayers 15 — which is NOT a platform
 * default and must never be enforced).
 *
 * Values are the PUBLIC ENFORCEMENT semantics (what the platform does when
 * nothing is set anywhere). Where enforcement and admin seeds historically
 * disagreed, the enforcement value won (recorded in ADR 0035):
 *   - requireCoaches: TRUE  (legacy registrations required a coach; admin seeds
 *     said false — the seeds were wrong)
 *   - maxCoaches: 2 (not the Settings form's 3)
 *   - min/maxTeamRepresentatives: 0 / 5 (not the wizard's 1 / 2)
 *
 * Storage rule unchanged: these values are NEVER written to documents
 * (ADR 0032 — unset means inherit). This table is read-side only.
 *
 * `maxPlayers: 0` and `capacity: 0` mean UNLIMITED — consumers own the
 * 0-semantics (getRosterLimits, waitlist); the resolver/wrapper never converts.
 */
import { DEFAULT_GAME_STATS_BY_SPORT } from '../schemas/stat';

// Sport-dependent entries are functions of the org's sport
// (organization.sports[0] — sport is single-sourced, see CLAUDE.md).
const SPORT_DEFAULT_GAME_DURATION: Record<string, number> = {
  pickleball: 30,
  volleyball: 60,
  // Adult standard; youth leagues stamp shorter durations at league-tier
  // config (never a schema default — config-hierarchy rule).
  soccer: 90,
};
const DEFAULT_GAME_DURATION_MINUTES = 60;

// Missing/unknown sport falls back to BASKETBALL (owner decision, ADR 0035) —
// consistent with getGameStats' legacy `|| 'basketball'` sport resolution.
export function defaultGameDurationInMinutes(sport?: string | null): number {
  return (sport && SPORT_DEFAULT_GAME_DURATION[sport]) || DEFAULT_GAME_DURATION_MINUTES;
}

export function defaultGameStats(sport?: string | null): string[] {
  return (sport && DEFAULT_GAME_STATS_BY_SPORT[sport]) || DEFAULT_GAME_STATS_BY_SPORT.basketball;
}

export const DEFAULT_MIN_PLAYERS = 1;
export const DEFAULT_MAX_PLAYERS = 0; // 0 = unlimited
export const DEFAULT_PROMOTION_PAYMENT_WINDOW_HOURS = 48;

export interface StaticConfigDefaults {
  requirePlayers: boolean;
  minPlayers: number;
  maxPlayers: number;
  requireCoaches: boolean;
  minCoaches: number;
  maxCoaches: number;
  requireTeamRepresentatives: boolean;
  minTeamRepresentatives: number;
  maxTeamRepresentatives: number;
  disableSync: boolean;
  hideTeams: boolean;
  hideSchedules: boolean;
  hideStandings: boolean;
  showStats: boolean;
  showAllTeams: boolean;
  excludeTeamName: boolean;
  hasWaitlist: boolean;
  autoPromote: boolean;
  promotionPaymentWindowHours: number;
  capacity: number;
  breakPlayoffTiesBySeed: boolean;
  statDisplayMode: 'average' | 'total';
  tiebreakers: Record<string, unknown[]>;
  eligibility: Record<string, unknown>;
  paymentProduct: undefined;
  donationProduct: undefined;
}

export const CONFIG_DEFAULTS: Readonly<StaticConfigDefaults> = Object.freeze({
  requirePlayers: true,
  minPlayers: DEFAULT_MIN_PLAYERS,
  maxPlayers: DEFAULT_MAX_PLAYERS,
  requireCoaches: true,
  minCoaches: 1,
  maxCoaches: 2,
  requireTeamRepresentatives: false,
  minTeamRepresentatives: 0,
  maxTeamRepresentatives: 5,
  disableSync: false,
  hideTeams: false,
  hideSchedules: false,
  hideStandings: false,
  showStats: true,
  showAllTeams: false,
  excludeTeamName: false,
  hasWaitlist: false,
  autoPromote: false,
  promotionPaymentWindowHours: DEFAULT_PROMOTION_PAYMENT_WINDOW_HOURS,
  capacity: 0, // 0 = unlimited
  breakPlayoffTiesBySeed: false,
  statDisplayMode: 'average' as const,
  tiebreakers: Object.freeze({}) as Record<string, unknown[]>,
  eligibility: Object.freeze({}) as Record<string, unknown>,
  // Refs have no default — an unset fee/donation stays undefined.
  paymentProduct: undefined,
  donationProduct: undefined,
});
